import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const ThemeContext = createContext(null);

const DEFAULTS = {
  id: "site",
  colors: { bg:"#060A14", surface:"#0A1121", text:"#E5E4E2", muted:"#94A3B8", accent:"#C0C0C0", accent_alt:"#1E3A8A", success:"#10B981", danger:"#EF4444" },
  fonts: { heading:"Outfit", body:"Manrope" },
  hero: { label:"LEOVERA NUTRITION", title:"Forza naturale.\nPrecisione d'élite.", subtitle:"Linea premium di integratori sportivi e benessere selezionati dai migliori marchi italiani.", cta_primary:"Esplora il catalogo", cta_secondary:"Parla con Leo" },
  tiers: {
    start_label:"Linea Start", power_label:"Linea Power", elite_label:"Linea Elite",
    start_name:"MUSCLE", start_subtitle:"Benessere & Fondamenta",
    start_description:"La base solida del tuo percorso. Vitamine, minerali e nutrienti essenziali per attivare ogni giorno la tua energia naturale.",
    power_name:"PERFORMANCE", power_subtitle:"Muscolo & Crescita",
    power_description:"Spinta, recupero e costruzione. Le formule che alimentano l'allenamento e accelerano la tua trasformazione fisica.",
    elite_name:"WELLNESS", elite_subtitle:"Performance Professionali",
    elite_description:"Il vertice della nutrizione sportiva. Formulazioni di precisione per chi non accetta compromessi.",
  },
  section_colors: { hero:"", bundle:"", catalog_start:"", catalog_power:"", catalog_elite:"", brand:"", footer:"" },
  labels: { price_hidden_text:"Prezzo su consulenza", out_of_stock_text:"Esaurito", add_to_cart_text:"Aggiungi al carrello", footer_copyright:"© 2026 LEOVERA NUTRITION", version:"V104.4" },
  layout: { hide_prices:true, show_forum:true, show_loyalty:true, show_recommended:true, show_price_comparison:true, recommended_badge_label:"Consigliato", rounded:"sm" },
  extra_colors: [], extra_fonts: [], extra_labels: [], extra_tiers: [], extra_hero_blocks: [],
  custom_css: "",
  logo_url: "", logo_width: 180, logo_alignment: "center",
};

const applyToDocument = (s) => {
  const root = document.documentElement;
  // Inject CSS variables so existing components/Tailwind classes (text-[var(--…)]) can opt-in
  Object.entries(s.colors || {}).forEach(([k, v]) => {
    if (v) root.style.setProperty(`--lv-${k.replace(/_/g, "-")}`, v);
  });
  // Extra user-defined colors (slugify the key)
  (s.extra_colors || []).forEach((c) => {
    if (c?.key && c?.value) {
      const slug = c.key.toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      if (slug) root.style.setProperty(`--lv-x-${slug}`, c.value);
    }
  });
  // Body bg + text — apply to body so it works without rewriting components
  if (s.colors?.bg)   document.body.style.background = s.colors.bg;
  if (s.colors?.text) document.body.style.color = s.colors.text;
  // Full-site background image (optional) — exposed as a CSS var so
  // top-level containers can layer it under their own bg color.
  root.style.setProperty("--lv-bg-image", s.colors?.bg_image ? `url(${s.colors.bg_image})` : "none");
  if (s.colors?.bg_image) {
    document.body.style.backgroundImage = `url(${s.colors.bg_image})`;
    document.body.style.backgroundSize = "cover";
    document.body.style.backgroundPosition = "center";
    document.body.style.backgroundAttachment = "fixed";
    document.body.style.backgroundRepeat = "no-repeat";
  } else {
    document.body.style.backgroundImage = "";
  }
  // Load extra Google fonts on the fly
  (s.extra_fonts || []).forEach((f) => {
    if (!f?.name) return;
    const id = `lv-font-${f.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f.name)}:wght@300;400;600;700&display=swap`;
    document.head.appendChild(link);
  });
  // Inject custom CSS via a dedicated style tag
  let style = document.getElementById("lv-custom-css");
  if (!style) {
    style = document.createElement("style");
    style.id = "lv-custom-css";
    document.head.appendChild(style);
  }
  style.textContent = s.custom_css || "";
};

export const ThemeProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings", { params: { _t: Date.now() } });
      setSettings({
        ...DEFAULTS,
        ...data,
        colors: { ...DEFAULTS.colors, ...(data.colors || {}) },
        fonts: { ...DEFAULTS.fonts, ...(data.fonts || {}) },
        hero: { ...DEFAULTS.hero, ...(data.hero || {}) },
        tiers: { ...DEFAULTS.tiers, ...(data.tiers || {}) },
        labels: { ...DEFAULTS.labels, ...(data.labels || {}) },
        layout: { ...DEFAULTS.layout, ...(data.layout || {}) },
        extra_colors: Array.isArray(data.extra_colors) ? data.extra_colors : [],
        extra_fonts: Array.isArray(data.extra_fonts) ? data.extra_fonts : [],
        extra_labels: Array.isArray(data.extra_labels) ? data.extra_labels : [],
        extra_tiers: Array.isArray(data.extra_tiers) ? data.extra_tiers : [],
        extra_hero_blocks: Array.isArray(data.extra_hero_blocks) ? data.extra_hero_blocks : [],
      });
    } catch {
      setSettings(DEFAULTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { applyToDocument(settings); }, [settings]);

  const value = useMemo(() => ({ settings, loading, refresh, setSettings }), [settings, loading, refresh]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
};
